import React, { useState, useMemo, useCallback } from 'react'
import { Plus, Search, Edit, Trash2, Calendar, Clock, Truck, User, MapPin, Package, IndianRupee } from 'lucide-react'
import { useData } from '../context/DataContext'
import Modal from '../components/Modal'
import DeleteConfirmModal from '../components/DeleteConfirmModal'

function Rental() {
  const { state, addRental, updateRental, deleteRental } = useData()
  const { rentals, customers, vehicles, loading, error } = state

  const [showForm, setShowForm] = useState(false)
  const [editingRental, setEditingRental] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterCustomer, setFilterCustomer] = useState('all')
  const [filterVehicle, setFilterVehicle] = useState('all')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, rental: null })

  // Form state
  const [formData, setFormData] = useState({
    customerId: '',
    vehicleId: '',
    rentalItems: [
      {
        startingPoint: '',
        startingTotalUnits: '',
        startingDate: new Date().toISOString().split('T')[0],
        startingTypeOfMaterial: '',
        endingPoint: '',
        endingTotalUnits: '',
        endingDate: new Date().toISOString().split('T')[0],
        endingTypeOfMaterial: '',
        amount: ''
      }
    ],
    totalAmount: '',
    givenAmount: '',
    notes: ''
  })

  const [errors, setErrors] = useState({})

  const filteredRentals = useMemo(() => {
    return rentals.filter(rental => {
      const customer = customers.find(c => c.id === rental.customerId)
      const vehicle = vehicles.find(v => v.id === rental.vehicleId)
      const customerName = customer?.name || ''
      const vehicleNumber = vehicle?.registrationNumber || ''
      
      const matchesSearch = searchTerm === '' || 
        customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vehicleNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        rental.startingPoint?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        rental.typeOfMaterial?.toLowerCase().includes(searchTerm.toLowerCase())
      
      const matchesCustomer = filterCustomer === 'all' || rental.customerId === filterCustomer
      const matchesVehicle = filterVehicle === 'all' || rental.vehicleId === filterVehicle
      
      // Date range filtering
      let matchesDateRange = true
      if (fromDate || toDate) {
        // Get rental date - use rentalDate if available, otherwise use first rental item's starting date
        const rentalDateStr = rental.rentalDate || (rental.rentalItems && rental.rentalItems.length > 0 ? rental.rentalItems[0].startingDate : null)
        
        if (rentalDateStr) {
          // Normalize dates to YYYY-MM-DD format for proper comparison (avoid timezone issues)
          const rentalDate = new Date(rentalDateStr)
          const rentalDateNormalized = new Date(rentalDate.getFullYear(), rentalDate.getMonth(), rentalDate.getDate())
          
          if (fromDate) {
            const fromDateObj = new Date(fromDate)
            const fromDateNormalized = new Date(fromDateObj.getFullYear(), fromDateObj.getMonth(), fromDateObj.getDate())
            matchesDateRange = matchesDateRange && rentalDateNormalized >= fromDateNormalized
          }
          
          if (toDate) {
            const toDateObj = new Date(toDate)
            const toDateNormalized = new Date(toDateObj.getFullYear(), toDateObj.getMonth(), toDateObj.getDate())
            matchesDateRange = matchesDateRange && rentalDateNormalized <= toDateNormalized
          }
        } else {
          // If no date is available, exclude from results when date filter is active
          matchesDateRange = false
        }
      }
      
      return matchesSearch && matchesCustomer && matchesVehicle && matchesDateRange
    })
  }, [rentals, searchTerm, filterCustomer, filterVehicle, fromDate, toDate, customers, vehicles])

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault()
    
    // Validation
    const newErrors = {}
    if (!formData.customerId) newErrors.customerId = 'Customer is required'
    if (!formData.vehicleId) newErrors.vehicleId = 'Vehicle is required'
    if (!formData.givenAmount) newErrors.givenAmount = 'Given amount is required'
    
    // Validate rental items
    formData.rentalItems.forEach((item, index) => {
      if (!item.startingPoint) newErrors[`startingPoint_${index}`] = 'Starting point is required'
      if (!item.startingTotalUnits) newErrors[`startingTotalUnits_${index}`] = 'Starting total units is required'
      if (!item.startingTypeOfMaterial) newErrors[`startingTypeOfMaterial_${index}`] = 'Starting type of material is required'
      if (!item.endingPoint) newErrors[`endingPoint_${index}`] = 'Ending point is required'
      if (!item.endingTotalUnits) newErrors[`endingTotalUnits_${index}`] = 'Ending total units is required'
      if (!item.endingTypeOfMaterial) newErrors[`endingTypeOfMaterial_${index}`] = 'Ending type of material is required'
      if (!item.amount) newErrors[`amount_${index}`] = 'Amount is required'
    })
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    try {
      if (editingRental) {
        const result = await updateRental(editingRental.id, formData)
        if (result.success) {
          setShowForm(false)
          setEditingRental(null)
          resetForm()
        }
      } else {
        const result = await addRental(formData)
        if (result.success) {
          setShowForm(false)
          resetForm()
        }
      }
    } catch (error) {
      console.error('Error submitting rental:', error)
    }
  }, [formData, editingRental, addRental, updateRental])

  const resetForm = () => {
    setFormData({
      customerId: '',
      vehicleId: '',
      rentalItems: [
        {
          startingPoint: '',
          startingTotalUnits: '',
          startingDate: new Date().toISOString().split('T')[0],
          startingTypeOfMaterial: '',
          endingPoint: '',
          endingTotalUnits: '',
          endingDate: new Date().toISOString().split('T')[0],
          endingTypeOfMaterial: '',
          amount: ''
        }
      ],
      totalAmount: '',
      givenAmount: '',
      notes: ''
    })
    setErrors({})
  }

  const handleEditRental = useCallback((rental) => {
    setEditingRental(rental)
    setFormData({
      customerId: rental.customerId || '',
      vehicleId: rental.vehicleId || '',
      rentalItems: rental.rentalItems || [
        {
          startingPoint: '',
          startingTotalUnits: '',
          startingDate: new Date().toISOString().split('T')[0],
          startingTypeOfMaterial: '',
          endingPoint: '',
          endingTotalUnits: '',
          endingDate: new Date().toISOString().split('T')[0],
          endingTypeOfMaterial: '',
          amount: ''
        }
      ],
      totalAmount: rental.totalAmount || '',
      givenAmount: rental.givenAmount || '',
      notes: rental.notes || ''
    })
    setErrors({})
    setShowForm(true)
  }, [])

  const handleDeleteRental = useCallback(async (rentalId) => {
    try {
      const result = await deleteRental(rentalId)
      if (!result.success) {
        alert(`Error deleting rental: ${result.error}`)
      }
    } catch (error) {
      console.error('Error deleting rental:', error)
      alert('Error deleting rental. Please try again.')
    }
  }, [deleteRental])

  const openDeleteModal = useCallback((rental) => {
    setDeleteModal({ isOpen: true, rental })
  }, [])

  const closeDeleteModal = useCallback(() => {
    setDeleteModal({ isOpen: false, rental: null })
  }, [])

  const confirmDelete = useCallback(() => {
    if (deleteModal.rental) {
      handleDeleteRental(deleteModal.rental.id)
    }
  }, [deleteModal.rental, handleDeleteRental])

  const handleCancelEdit = useCallback(() => {
    setShowForm(false)
    setEditingRental(null)
    resetForm()
  }, [])

  // Add new rental item
  const addRentalItem = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      rentalItems: [
        ...prev.rentalItems,
        {
          startingPoint: '',
          startingTotalUnits: '',
          startingDate: new Date().toISOString().split('T')[0],
          startingTypeOfMaterial: '',
          endingPoint: '',
          endingTotalUnits: '',
          endingDate: new Date().toISOString().split('T')[0],
          endingTypeOfMaterial: '',
          amount: ''
        }
      ]
    }))
  }, [])

  // Remove rental item
  const removeRentalItem = useCallback((index) => {
    setFormData(prev => {
      const updatedItems = prev.rentalItems.filter((_, i) => i !== index)
      
      // Calculate total amount from remaining items
      const calculatedTotal = updatedItems.reduce((sum, item) => {
        return sum + parseFloat(item.amount || 0)
      }, 0)
      
      return {
        ...prev,
        rentalItems: updatedItems,
        totalAmount: calculatedTotal.toString()
      }
    })
  }, [])

  // Update rental item
  const updateRentalItem = useCallback((index, field, value) => {
    setFormData(prev => {
      const updatedItems = prev.rentalItems.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
      
      // Calculate total amount from all items
      const calculatedTotal = updatedItems.reduce((sum, item) => {
        return sum + parseFloat(item.amount || 0)
      }, 0)
      
      return {
        ...prev,
        rentalItems: updatedItems,
        totalAmount: calculatedTotal.toString()
      }
    })
  }, [])

  // Calculate summary statistics
  const totalRentals = filteredRentals.length
  const totalAmount = filteredRentals.reduce((sum, rental) => sum + parseFloat(rental.totalAmount || 0), 0)
  const totalGivenAmount = filteredRentals.reduce((sum, rental) => sum + parseFloat(rental.givenAmount || 0), 0)
  const totalPendingAmount = totalAmount - totalGivenAmount
  const totalUnits = filteredRentals.reduce((sum, rental) => sum + parseFloat(rental.totalUnits || 0), 0)

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0)
  }

  return (
    <div className="container mx-auto px-4 py-2">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Rental Management</h1>
          <p className="text-gray-600">Track vehicle rentals and material transport</p>
        </div>
        <div className="mt-4 sm:mt-0 flex flex-col sm:flex-row gap-3">
        <button
          onClick={() => {
            setShowForm(true)
            setEditingRental(null)
            resetForm()
          }}
            className="btn btn-primary flex items-center space-x-2"
        >
            <Plus className="h-5 w-5" />
            <span>Add Rental</span>
        </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Package className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
            </div>
            <div className="ml-2 sm:ml-3">
              <p className="text-sm font-medium text-gray-600">Total Rentals</p>
              <p className="text-base sm:text-lg font-semibold text-gray-900">{totalRentals}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <IndianRupee className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
            </div>
            <div className="ml-2 sm:ml-3">
              <p className="text-sm font-medium text-gray-600">Total Amount</p>
              <p className="text-base sm:text-lg font-semibold text-gray-900">{formatCurrency(totalAmount)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 rounded-lg">
              <IndianRupee className="h-4 w-4 sm:h-5 sm:w-5 text-orange-600" />
            </div>
            <div className="ml-2 sm:ml-3">
              <p className="text-sm font-medium text-gray-600">Given Amount</p>
              <p className="text-base sm:text-lg font-semibold text-gray-900">{formatCurrency(totalGivenAmount)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <IndianRupee className="h-4 w-4 sm:h-5 sm:w-5 text-red-600" />
            </div>
            <div className="ml-2 sm:ml-3">
              <p className="text-sm font-medium text-gray-600">Pending Amount</p>
              <p className="text-base sm:text-lg font-semibold text-gray-900">{formatCurrency(totalPendingAmount)}</p>
            </div>
          </div>
        </div>
      </div>

                           {/* Filters */}
        <div className="bg-white p-3 sm:p-4 lg:p-6 rounded-lg shadow-sm border border-gray-200 my-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 sm:gap-4">
            {/* Search */}
            <div className="lg:col-span-2">
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3 w-3 sm:h-4 sm:w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by customer, vehicle, starting point, material..."
                  className="filter-input pl-8 sm:pl-10 text-xs sm:text-sm"
                />
              </div>
            </div>

            {/* Customer Filter */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Customer</label>
              <select
                value={filterCustomer}
                onChange={(e) => setFilterCustomer(e.target.value)}
                className="filter-input text-gray-900 text-xs sm:text-sm"
              >
                <option value="all" className="text-gray-900">All Customers</option>
                {customers.map(customer => (
                  <option key={customer.id} value={customer.id} className="text-gray-900">
                    {customer.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Vehicle Filter */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Vehicle</label>
              <select
                value={filterVehicle}
                onChange={(e) => setFilterVehicle(e.target.value)}
                className="filter-input text-gray-900 text-xs sm:text-sm"
              >
                <option value="all" className="text-gray-900">All Vehicles</option>
                {vehicles.map(vehicle => (
                  <option key={vehicle.id} value={vehicle.id} className="text-gray-900">
                    {vehicle.registrationNumber} - {vehicle.type}
                  </option>
                ))}
              </select>
            </div>

            {/* From Date */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">From Date</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="filter-input text-gray-900 text-xs sm:text-sm"
                max={toDate || undefined}
              />
            </div>

            {/* To Date */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">To Date</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="filter-input text-gray-900 text-xs sm:text-sm"
                min={fromDate || undefined}
              />
            </div>
          </div>
          
          {/* Clear Filters Button */}
          <div className="mt-3 sm:mt-4 flex justify-end">
            <button
              onClick={() => {
                setSearchTerm('')
                setFilterCustomer('all')
                setFilterVehicle('all')
                setFromDate('')
                setToDate('')
              }}
              className="px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm bg-gradient-to-r from-brand-orange to-brand-red text-white rounded-lg hover:from-orange-500 hover:to-red-600 transition-all duration-200 font-medium"
            >
              Clear All Filters
            </button>
          </div>
        </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-red mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading rentals...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800">Error: {error}</p>
        </div>
      )}

      {/* Rentals List */}
      {!loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredRentals.length > 0 ? (
            filteredRentals
              .sort((a, b) => new Date(b.rentalDate) - new Date(a.rentalDate))
              .map((rental) => {
                const customer = customers.find(c => c.id === rental.customerId)
                const vehicle = vehicles.find(v => v.id === rental.vehicleId)
                
                return (
                  <div key={rental.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
                    {/* Rental Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                          {customer?.name || 'Unknown Customer'}
                        </h3>
                        <div className="flex items-center text-sm text-gray-600 mb-2">
                          <Truck className="h-4 w-4 mr-2 text-gray-400" />
                          <span>{vehicle?.registrationNumber || 'N/A'} - {vehicle?.type || 'N/A'}</span>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditRental(rental)}
                          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                          title="Edit rental"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        {/* Delete button hidden */}
                        {/* <button
                          onClick={() => openDeleteModal(rental)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
                          title="Delete rental"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button> */}
                      </div>
                    </div>

                    {/* Location Information */}
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center text-sm text-gray-600">
                        <MapPin className="h-4 w-4 mr-2 text-gray-400" />
                        <span>From: {rental.rentalItems?.[0]?.startingPoint || 'N/A'}</span>
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <MapPin className="h-4 w-4 mr-2 text-gray-400" />
                        <span>To: {rental.rentalItems?.[0]?.endingPoint || 'N/A'}</span>
                      </div>
                    </div>

                    {/* Material Information */}
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center text-sm text-gray-600">
                        <Package className="h-4 w-4 mr-2 text-gray-400" />
                        <span>Material: {rental.rentalItems?.[0]?.startingTypeOfMaterial || 'N/A'}</span>
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                        <span>Date: {rental.rentalItems?.[0]?.startingDate ? new Date(rental.rentalItems[0].startingDate).toLocaleDateString() : 'N/A'}</span>
                      </div>
                    </div>

                    {/* Amount Information */}
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center text-sm text-gray-600">
                        <IndianRupee className="h-4 w-4 mr-2 text-gray-400" />
                        <span>Total Amount: {formatCurrency(rental.totalAmount)}</span>
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <IndianRupee className="h-4 w-4 mr-2 text-gray-400" />
                        <span>Given Amount: {formatCurrency(rental.givenAmount)}</span>
                      </div>
                    </div>

                    {/* Notes */}
                    {rental.notes && (
                      <div className="text-xs text-gray-400 border-t border-gray-100 pt-3">
                        <div>Notes: {rental.notes}</div>
                      </div>
                    )}

                    {/* Payment Status Badge */}
                    <div className="mt-4 pt-3 border-t border-gray-100 flex justify-start">
                      <span className={`inline-flex px-3 py-2 text-sm font-semibold rounded-full ${
                        parseFloat(rental.givenAmount || 0) >= parseFloat(rental.totalAmount || 0) 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-orange-100 text-orange-800'
                      }`}>
                        {parseFloat(rental.givenAmount || 0) >= parseFloat(rental.totalAmount || 0) ? 'FULLY PAID' : 'PENDING'}
                      </span>
                    </div>
                  </div>
                )
              })
          ) : (
            <div className="col-span-full">
              <div className="text-center py-12">
                <div className="bg-gray-100 rounded-full p-3 w-16 h-16 mx-auto mb-4">
                  <Package className="h-10 w-10 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {searchTerm ? `No results found for "${searchTerm}"` : 'No rentals yet'}
                </h3>
                <p className="text-gray-500 mb-4">
                  {searchTerm 
                    ? 'Try searching for: customer names, vehicle numbers, starting points, or material types'
                    : filterCustomer !== 'all' || filterVehicle !== 'all' || fromDate || toDate
                    ? 'Try adjusting your filter criteria'
                    : 'Get started by adding your first rental'
                  }
                </p>
                {!searchTerm && filterCustomer === 'all' && filterVehicle === 'all' && !fromDate && !toDate && (
                  <button
                    onClick={() => {
                      setShowForm(true)
                      setEditingRental(null)
                      resetForm()
                    }}
                    className="btn btn-primary"
                  >
                    Add Your First Rental
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Rental Form Modal */}
      <Modal
        isOpen={showForm}
        onClose={handleCancelEdit}
        title={editingRental ? 'Edit Rental' : 'Add New Rental'}
        size="max-w-4xl"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Common Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Common Information</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Customer *
                </label>
                <select
                  value={formData.customerId}
                  onChange={(e) => setFormData({...formData, customerId: e.target.value})}
                  className={`input ${errors.customerId ? 'border-red-500 focus:ring-red-500' : ''}`}
                >
                  <option value="">Choose a customer...</option>
                  {customers.map(customer => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name} {customer.company ? `(${customer.company})` : ''}
                    </option>
                  ))}
                </select>
                {errors.customerId && (
                  <p className="mt-1 text-sm text-red-600">{errors.customerId}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Vehicle *
                </label>
                <select
                  value={formData.vehicleId}
                  onChange={(e) => setFormData({...formData, vehicleId: e.target.value})}
                  className={`input ${errors.vehicleId ? 'border-red-500 focus:ring-red-500' : ''}`}
                >
                  <option value="">Choose a vehicle...</option>
                  {vehicles.map(vehicle => (
                    <option key={vehicle.id} value={vehicle.id}>
                      {vehicle.registrationNumber} - {vehicle.type}
                    </option>
                  ))}
                </select>
                {errors.vehicleId && (
                  <p className="mt-1 text-sm text-red-600">{errors.vehicleId}</p>
                )}
              </div>
            </div>
          </div>

          {/* Rental Items */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Rental Items</h3>
              <button
                type="button"
                onClick={addRentalItem}
                className="inline-flex items-center px-3 py-2 bg-gradient-to-r from-brand-orange to-brand-red text-white rounded-lg hover:from-orange-500 hover:to-red-600 transition-all duration-200 text-sm font-medium"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Item
              </button>
            </div>

            {formData.rentalItems.map((item, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-md font-medium text-gray-900">Item {index + 1}</h4>
                  {formData.rentalItems.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeRentalItem(index)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 p-1 rounded"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>

                {/* Starting Point Section */}
                <div className="mb-6">
                  <h5 className="text-sm font-semibold text-gray-700 mb-3 border-b border-gray-200 pb-2">Starting Point</h5>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Starting Point *
                      </label>
                      <input
                        type="text"
                        value={item.startingPoint}
                        onChange={(e) => updateRentalItem(index, 'startingPoint', e.target.value)}
                        placeholder="Enter starting point"
                        className={`input ${errors[`startingPoint_${index}`] ? 'border-red-500 focus:ring-red-500' : ''}`}
                      />
                      {errors[`startingPoint_${index}`] && (
                        <p className="mt-1 text-sm text-red-600">{errors[`startingPoint_${index}`]}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Total Units *
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={item.startingTotalUnits}
                        onChange={(e) => updateRentalItem(index, 'startingTotalUnits', e.target.value)}
                        placeholder="Enter total units"
                        className={`input ${errors[`startingTotalUnits_${index}`] ? 'border-red-500 focus:ring-red-500' : ''}`}
                      />
                      {errors[`startingTotalUnits_${index}`] && (
                        <p className="mt-1 text-sm text-red-600">{errors[`startingTotalUnits_${index}`]}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Date *
                      </label>
                      <input
                        type="date"
                        value={item.startingDate}
                        onChange={(e) => updateRentalItem(index, 'startingDate', e.target.value)}
                        className="input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Type of Material *
                      </label>
                      <input
                        type="text"
                        value={item.startingTypeOfMaterial}
                        onChange={(e) => updateRentalItem(index, 'startingTypeOfMaterial', e.target.value)}
                        placeholder="Enter type of material"
                        className={`input ${errors[`startingTypeOfMaterial_${index}`] ? 'border-red-500 focus:ring-red-500' : ''}`}
                      />
                      {errors[`startingTypeOfMaterial_${index}`] && (
                        <p className="mt-1 text-sm text-red-600">{errors[`startingTypeOfMaterial_${index}`]}</p>
                      )}
                    </div>
                  </div>
                </div>

                                 {/* Ending Point Section */}
                 <div className="mb-6">
                   <h5 className="text-sm font-semibold text-gray-700 mb-3 border-b border-gray-200 pb-2">Ending Point</h5>
                   <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">
                         Ending Point *
                       </label>
                       <input
                         type="text"
                         value={item.endingPoint}
                         onChange={(e) => updateRentalItem(index, 'endingPoint', e.target.value)}
                         placeholder="Enter ending point"
                         className={`input ${errors[`endingPoint_${index}`] ? 'border-red-500 focus:ring-red-500' : ''}`}
                       />
                       {errors[`endingPoint_${index}`] && (
                         <p className="mt-1 text-sm text-red-600">{errors[`endingPoint_${index}`]}</p>
                       )}
                     </div>

                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">
                         Total Units *
                       </label>
                       <input
                         type="number"
                         step="0.01"
                         value={item.endingTotalUnits}
                         onChange={(e) => updateRentalItem(index, 'endingTotalUnits', e.target.value)}
                         placeholder="Enter total units"
                         className={`input ${errors[`endingTotalUnits_${index}`] ? 'border-red-500 focus:ring-red-500' : ''}`}
                       />
                       {errors[`endingTotalUnits_${index}`] && (
                         <p className="mt-1 text-sm text-red-600">{errors[`endingTotalUnits_${index}`]}</p>
                       )}
                     </div>

                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">
                         Date *
                       </label>
                       <input
                         type="date"
                         value={item.endingDate}
                         onChange={(e) => updateRentalItem(index, 'endingDate', e.target.value)}
                         className="input"
                       />
                     </div>

                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">
                         Type of Material *
                       </label>
                       <input
                         type="text"
                         value={item.endingTypeOfMaterial}
                         onChange={(e) => updateRentalItem(index, 'endingTypeOfMaterial', e.target.value)}
                         placeholder="Enter type of material"
                         className={`input ${errors[`endingTypeOfMaterial_${index}`] ? 'border-red-500 focus:ring-red-500' : ''}`}
                       />
                       {errors[`endingTypeOfMaterial_${index}`] && (
                         <p className="mt-1 text-sm text-red-600">{errors[`endingTypeOfMaterial_${index}`]}</p>
                       )}
                     </div>
                   </div>
                 </div>

                 {/* Amount Section */}
                 <div>
                   <h5 className="text-sm font-semibold text-gray-700 mb-3 border-b border-gray-200 pb-2">Amount</h5>
                   <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">
                         Amount *
                       </label>
                       <input
                         type="number"
                         step="0.01"
                         value={item.amount}
                         onChange={(e) => updateRentalItem(index, 'amount', e.target.value)}
                         placeholder="Enter amount"
                         className={`input ${errors[`amount_${index}`] ? 'border-red-500 focus:ring-red-500' : ''}`}
                       />
                       {errors[`amount_${index}`] && (
                         <p className="mt-1 text-sm text-red-600">{errors[`amount_${index}`]}</p>
                       )}
                     </div>
                   </div>
                 </div>
              </div>
            ))}
          </div>

          {/* Summary Section */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Summary</h3>
                         <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
               <div>
                 <label className="block text-sm font-medium text-gray-700 mb-1">
                   Total Amount (Auto-calculated)
                 </label>
                 <input
                   type="number"
                   step="0.01"
                   value={formData.totalAmount}
                   readOnly
                   className="input bg-gray-100 cursor-not-allowed"
                 />
                 <p className="mt-1 text-sm text-gray-500">Calculated from rental items</p>
               </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Given Amount *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.givenAmount}
                  onChange={(e) => setFormData({...formData, givenAmount: e.target.value})}
                  placeholder="Enter given amount"
                  className={`input ${errors.givenAmount ? 'border-red-500 focus:ring-red-500' : ''}`}
                />
                {errors.givenAmount && (
                  <p className="mt-1 text-sm text-red-600">{errors.givenAmount}</p>
                )}
              </div>
            </div>

            {/* Notes */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Enter any additional notes..."
                rows={3}
                className="input"
              />
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleCancelEdit}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-orange"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-brand-orange to-brand-red rounded-lg hover:from-orange-500 hover:to-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-orange disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : (editingRental ? 'Update Rental' : 'Add Rental')}
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        title="Delete Rental"
        message="Are you sure you want to delete this rental record?"
        itemName={deleteModal.rental ? `Rental for ${customers.find(c => c.id === deleteModal.rental.customerId)?.name || 'Customer'}` : 'Rental'}
      />
    </div>
  )
}

export default Rental
